import React, { useEffect, useState, useCallback } from 'react';
import { FaPlus, FaTrash, FaTimes, FaCertificate, FaCheck, FaPaperclip } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import { openPdfInline } from '../PdfViewer/PdfViewerHost';
import './ManageQualificationsModal.css';

// Used by the contractor admin to add / remove qualifications on one of their
// engineers. Also usable by cloud_staff / admins for any user.
const ManageQualificationsModal = ({ user, onClose, onChanged }) => {
  const [taxonomy, setTaxonomy] = useState([]);
  const [held, setHeld] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [add, setAdd] = useState({ qualification_id: '', awarded_at: '', expires_at: '' });

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [tR, hR] = await Promise.all([
        fetch(getApiEndpoint('/api/qualifications'), { headers: getAuthHeader() }).then(r => r.ok ? r.json() : { qualifications: [] }),
        fetch(getApiEndpoint(`/api/users/${user.id}/qualifications`), { headers: getAuthHeader() }).then(r => r.ok ? r.json() : { qualifications: [] }),
      ]);
      setTaxonomy(Array.isArray(tR.qualifications) ? tR.qualifications : []);
      setHeld(Array.isArray(hR.qualifications) ? hR.qualifications : []);
    } catch (_) { toast.error('Could not load qualifications'); }
    finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  // Show every qualification in the taxonomy; disable ones the engineer
  // already has (pending or approved) so the list matches the engineer's
  // self-service view and the FM job-creation picker.
  const heldQualIds = new Set(held.filter(h => h.status !== 'rejected').map(h => h.qualification_id));
  const available = taxonomy; // all of them; we disable the ones already held

  const addQual = async (e) => {
    e.preventDefault();
    if (!add.qualification_id) return toast.error('Pick a qualification');
    setBusy(true);
    try {
      const body = {
        qualification_id: Number(add.qualification_id),
        awarded_at: add.awarded_at || null,
        expires_at: add.expires_at || null,
      };
      const r = await fetch(getApiEndpoint(`/api/users/${user.id}/qualifications`), {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok && r.status !== 202) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed');
      }
      toast.success('Qualification added');
      setAdd({ qualification_id: '', awarded_at: '', expires_at: '' });
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (uq) => {
    if (!window.confirm(`Remove "${uq.qualification_name}" from ${user.first_name || user.email}?`)) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/users/${user.id}/qualifications/${uq.id}`), {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      if (!r.ok && r.status !== 202) throw new Error();
      toast.success('Removed');
      load();
      onChanged && onChanged();
    } catch (_) { toast.error('Failed to remove'); }
  };

  const approve = async (uq) => {
    try {
      const r = await fetch(getApiEndpoint(`/api/users/${user.id}/qualifications/${uq.id}/approve`), {
        method: 'PATCH',
        headers: getAuthHeader(),
      });
      if (!r.ok) throw new Error();
      toast.success(`Approved: ${uq.qualification_name}`);
      load();
      onChanged && onChanged();
    } catch (_) { toast.error('Failed to approve'); }
  };

  const reject = async (uq) => {
    const reason = window.prompt('Reason for rejection (optional, shown to the engineer):', '');
    if (reason === null) return; // user cancelled
    try {
      const r = await fetch(getApiEndpoint(`/api/users/${user.id}/qualifications/${uq.id}/reject`), {
        method: 'PATCH',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!r.ok) throw new Error();
      toast.success('Rejected');
      load();
      onChanged && onChanged();
    } catch (_) { toast.error('Failed to reject'); }
  };

  const viewEvidence = async (uq) => {
    try {
      const r = await fetch(
        getApiEndpoint(`/api/users/${user.id}/qualifications/${uq.id}/evidence`),
        { headers: getAuthHeader() }
      );
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const isPdf = (uq.evidence_original_filename || '').toLowerCase().endsWith('.pdf') || blob.type === 'application/pdf';
      if (isPdf) {
        openPdfInline(url, uq.evidence_original_filename || 'Certificate');
        setTimeout(() => window.URL.revokeObjectURL(url), 5 * 60 * 1000);
      } else {
        window.open(url, '_blank');
      }
    } catch (_) { toast.error('Could not open evidence'); }
  };

  const isExpired = (uq) => uq.expires_at && new Date(uq.expires_at) < new Date();

  if (!user) return null;
  return (
    <div className="cs-mqm-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="cs-mqm-modal" onClick={e => e.stopPropagation()}>
        <div className="cs-mqm-header">
          <div>
            <h3>Qualifications</h3>
            <span className="cs-mqm-sub">
              <FaCertificate /> {user.first_name} {user.last_name} · {user.email}
            </span>
          </div>
          <button type="button" className="cs-mqm-close" onClick={onClose} aria-label="Close"><FaTimes /></button>
        </div>

        <div className="cs-mqm-body">
          {loading ? (
            <div className="cs-mqm-state">Loading…</div>
          ) : (
            <>
              {(() => {
                const pending = held.filter(h => h.status === 'pending');
                const approved = held.filter(h => h.status === 'approved' || !h.status);
                const rejected = held.filter(h => h.status === 'rejected');

                return (
                  <>
                    {pending.length > 0 && (
                      <>
                        <h4 className="cs-mqm-section-title cs-mqm-pending-title">
                          Pending approval ({pending.length})
                        </h4>
                        <ul className="cs-mqm-list">
                          {pending.map(uq => (
                            <li key={uq.id} className="cs-mqm-row pending">
                              <div>
                                <strong>{uq.qualification_name}</strong>
                                {uq.qualification_category && <span className="cs-mqm-cat"> · {uq.qualification_category}</span>}
                                <div className="cs-mqm-dates">
                                  Submitted {new Date(uq.created_at).toLocaleDateString('en-GB')}
                                  {uq.awarded_at && ` · Awarded ${new Date(uq.awarded_at).toLocaleDateString('en-GB')}`}
                                  {uq.expires_at && ` · Expires ${new Date(uq.expires_at).toLocaleDateString('en-GB')}`}
                                </div>
                              </div>
                              <div className="cs-mqm-actions">
                                {uq.evidence_original_filename && (
                                  <button type="button" onClick={() => viewEvidence(uq)} title="View certificate" className="cs-mqm-action">
                                    <FaPaperclip /> Cert
                                  </button>
                                )}
                                <button type="button" onClick={() => approve(uq)} title="Approve" className="cs-mqm-action approve">
                                  <FaCheck /> Approve
                                </button>
                                <button type="button" onClick={() => reject(uq)} title="Reject" className="cs-mqm-action reject">
                                  <FaTimes /> Reject
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    <h4 className="cs-mqm-section-title">Currently held</h4>
                    {approved.length === 0 ? (
                      <div className="cs-mqm-empty">No approved qualifications yet.</div>
                    ) : (
                      <ul className="cs-mqm-list">
                        {approved.map(uq => (
                          <li key={uq.id} className={`cs-mqm-row ${isExpired(uq) ? 'expired' : ''}`}>
                            <div>
                              <strong>{uq.qualification_name}</strong>
                              {uq.qualification_category && <span className="cs-mqm-cat"> · {uq.qualification_category}</span>}
                              <div className="cs-mqm-dates">
                                {uq.awarded_at && <>Awarded {new Date(uq.awarded_at).toLocaleDateString('en-GB')}</>}
                                {uq.awarded_at && uq.expires_at && ' · '}
                                {uq.expires_at && (
                                  <span className={isExpired(uq) ? 'cs-mqm-expired-text' : ''}>
                                    Expires {new Date(uq.expires_at).toLocaleDateString('en-GB')}
                                  </span>
                                )}
                                {!uq.awarded_at && !uq.expires_at && 'No dates recorded'}
                              </div>
                            </div>
                            <div className="cs-mqm-actions">
                              {uq.evidence_original_filename && (
                                <button type="button" onClick={() => viewEvidence(uq)} title="View certificate" className="cs-mqm-action">
                                  <FaPaperclip />
                                </button>
                              )}
                              <button type="button" className="cs-mqm-remove" onClick={() => remove(uq)} title="Remove">
                                <FaTrash />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    {rejected.length > 0 && (
                      <>
                        <h4 className="cs-mqm-section-title">Rejected</h4>
                        <ul className="cs-mqm-list">
                          {rejected.map(uq => (
                            <li key={uq.id} className="cs-mqm-row rejected">
                              <div>
                                <strong>{uq.qualification_name}</strong>
                                {uq.rejection_reason && <div className="cs-mqm-dates">Reason: {uq.rejection_reason}</div>}
                              </div>
                              <button type="button" className="cs-mqm-remove" onClick={() => remove(uq)} title="Remove">
                                <FaTrash />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                );
              })()}

              <h4 className="cs-mqm-section-title">Add a qualification</h4>
              {available.length === 0 ? (
                <div className="cs-mqm-empty">No qualifications configured in the taxonomy yet.</div>
              ) : heldQualIds.size >= available.length ? (
                <div className="cs-mqm-empty">This engineer holds every qualification in the taxonomy.</div>
              ) : (
                <form onSubmit={addQual} className="cs-mqm-form">
                  <label>
                    Qualification
                    <select
                      value={add.qualification_id}
                      onChange={e => setAdd({ ...add, qualification_id: e.target.value })}
                      required
                    >
                      <option value="">Pick one…</option>
                      {available.map(q => {
                        const alreadyHeld = heldQualIds.has(q.id);
                        return (
                          <option key={q.id} value={q.id} disabled={alreadyHeld}>
                            {q.name}{q.category ? ` (${q.category})` : ''}{alreadyHeld ? ' — already on file' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                  <div className="cs-mqm-form-row">
                    <label>
                      Awarded
                      <input type="date" value={add.awarded_at} onChange={e => setAdd({ ...add, awarded_at: e.target.value })} />
                    </label>
                    <label>
                      Expires (optional)
                      <input type="date" value={add.expires_at} onChange={e => setAdd({ ...add, expires_at: e.target.value })} />
                    </label>
                  </div>
                  <div className="cs-mqm-form-actions">
                    <button type="submit" disabled={busy} className="primary">
                      <FaPlus /> {busy ? 'Adding…' : 'Add qualification'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageQualificationsModal;
